import type {
  AuditLogDto,
  AutomationRuleDto,
  ExternalPlayerDto,
  IntegrationStatusDto,
  OrgChartNode,
  OrgUnitDto,
  OrgUnitTreeNode,
  PersonDto,
  RoleDto,
  TaskTemplateDto,
} from '@ukinfo/shared';
import type { Paged } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ClipboardEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useScope } from '../lib/scope';
import { parseTemplateRows } from '../lib/templateRows';
import { ORG_UNIT_TYPE_LABELS, formatDate, formatDateTime } from '../lib/format';
import { useConfirm } from '../components/ConfirmDialog';
import { OrgUnitSelect } from '../components/OrgUnitPicker';
import { PersonPicker, PersonSelect } from '../components/PersonPicker';
import { PageHeader } from '../components/PageHeader';
import { Pagination } from '../components/Pagination';
import { PersonImportDialog } from '../components/PersonImportDialog';
import { RoleActions } from '../components/PersonRoles';
import { PlayerSyncDialog } from '../components/PlayerSyncDialog';
import { AccessTab, RolesTab } from '../components/PermissionsAdmin';
import { AttachmentPicker, toChoices, toRefs, type AttachmentChoice } from '../components/Attachments';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Select,
  Tabs,
  Textarea,
  cn,
  useToast,
} from '../components/ui';

/** Pfadabschnitt bzw. `?reiter=` (deutsch) auf die Reiter-Kennung abbilden. */
const REITER_PFADE: Record<string, string> = {
  struktur: 'structure',
  personen: 'persons',
  organigramm: 'chart',
  funktionen: 'roles',
  zugriffsrechte: 'permissions',
  vorlagen: 'templates',
  automatisierungen: 'automations',
  verbindungen: 'integrations',
  protokoll: 'audit',
};

export function ClubPage(): JSX.Element {
  const { can } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const ausPfad = location.pathname.split('/').filter(Boolean)[1];
  const gewuenscht = REITER_PFADE[ausPfad ?? ''] ?? REITER_PFADE[params.get('reiter') ?? ''];
  const hervorgehoben = params.get('unit');
  const [tab, setTab] = useState(gewuenscht ?? 'structure');
  // Neue Ziele (z. B. aus Suche oder Meldung) wirken auch, wenn die Seite schon offen ist.
  useEffect(() => {
    if (gewuenscht) setTab(gewuenscht);
    else if (hervorgehoben) setTab('structure');
  }, [gewuenscht, hervorgehoben, location.key]);

  const tabs = [
    ...(can('organisation', 'read') ? [{ id: 'structure', label: 'Struktur' }] : []),
    ...(can('persons', 'read') ? [{ id: 'persons', label: 'Personen' }] : []),
    ...(can('organisation', 'read') ? [{ id: 'chart', label: 'Organigramm' }] : []),
    ...(can('permissions', 'read') ? [{ id: 'roles', label: 'Funktionen' }] : []),
    ...(can('permissions', 'read') ? [{ id: 'permissions', label: 'Zugriffsrechte' }] : []),
    ...(can('tasks', 'read') ? [{ id: 'templates', label: 'Vorlagen' }] : []),
    ...(can('tasks', 'manage') ? [{ id: 'automations', label: 'Automatisierungen' }] : []),
    ...(can('calendar', 'manage') ? [{ id: 'integrations', label: 'Verbindungen' }] : []),
    ...(can('permissions', 'read') ? [{ id: 'audit', label: 'Protokoll' }] : []),
  ];

  const activeTab = tabs.some((entry) => entry.id === tab) ? tab : (tabs[0]?.id ?? '');

  return (
    <div className="space-y-4">
      <PageHeader title="Verein" description="Struktur, Personen, Funktionen und Verbindungen" />

      <Tabs tabs={tabs} active={activeTab} onChange={setTab} />

      {activeTab === 'structure' && <StructureTab highlightId={hervorgehoben} />}
      {activeTab === 'persons' && <PersonsTab />}
      {activeTab === 'chart' && <OrgChartTab />}
      {activeTab === 'roles' && <RolesTab />}
      {activeTab === 'permissions' && <AccessTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'automations' && <AutomationsTab />}
      {activeTab === 'integrations' && <IntegrationsTab />}
      {activeTab === 'audit' && <AuditTab />}
    </div>
  );
}

/* ----------------------------------------------------------------- Struktur */

function StructureTab({ highlightId }: { highlightId: string | null }): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('SECTION');
  const [editing, setEditing] = useState<Pick<OrgUnitDto, 'id' | 'name' | 'type'> | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('SECTION');
  const [editParentId, setEditParentId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['organisation', 'tree'],
    queryFn: () => api.get<OrgUnitTreeNode[]>('/api/organisation/tree'),
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/organisation/units', { name, type, parentId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      toast.show('Bereich angelegt');
      setName('');
      setDialogOpen(false);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen', 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/organisation/units/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      toast.show('Bereich entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });
  const update = useMutation({
    mutationFn: () => api.patch(`/api/organisation/units/${editing?.id}`, {
      name: editName,
      type: editType,
      parentId: editParentId,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      toast.show('Bereich gespeichert');
      setEditing(null);
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Bereich konnte nicht gespeichert werden', 'error'),
  });

  const renderNode = (node: OrgUnitTreeNode, depth: number, parentIdForNode: string | null = null): JSX.Element => (
    <div key={node.id}>
      <div
        ref={node.id === highlightId ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
        className={cn(
          'flex items-center gap-2 rounded-lg py-1.5 hover:bg-slate-50',
          node.id === highlightId && 'bg-brand-50 ring-2 ring-brand-500',
        )}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <span className={cn('flex-1 text-sm', depth === 0 && 'font-semibold')}>{node.name}</span>
        <Badge tone="neutral">{ORG_UNIT_TYPE_LABELS[node.type] ?? node.type}</Badge>
        {can('organisation', 'create') && (
          <button
            type="button"
            className="text-xs text-brand-700 hover:underline"
            onClick={() => {
              setParentId(node.id);
              setDialogOpen(true);
            }}
          >
            + Unterbereich
          </button>
        )}
        {can('organisation', 'update') && depth > 0 && (
          <button type="button" className="text-xs text-brand-700 hover:underline" onClick={() => {
            setEditing({ id: node.id, name: node.name, type: node.type });
            setEditName(node.name);
            setEditType(node.type);
            setEditParentId(parentIdForNode);
          }}>bearbeiten</button>
        )}
        {can('organisation', 'delete') && node.children.length === 0 && depth > 0 && (
          <button
            type="button"
            className="text-xs text-slate-400 hover:text-rose-600"
            onClick={() => {
              if (window.confirm(`Bereich "${node.name}" entfernen?`)) remove.mutate(node.id);
            }}
          >
            entfernen
          </button>
        )}
      </div>
      {node.children.map((child) => renderNode(child, depth + 1, node.id))}
    </div>
  );

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;

  return (
    <>
      <Card>
        <CardBody>
          <SectionTitle
            title="Vereinsstruktur"
            subtitle="Beliebig tiefe Gliederung – Sektionen, Gruppen, Mannschaften"
          />
          {(data ?? []).map((node) => renderNode(node, 0))}
        </CardBody>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Neuer Bereich"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name}>
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" required>
            {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Art">
            {(id) => (
              <Select id={id} value={type} onChange={(e) => setType(e.target.value)}>
                {Object.entries(ORG_UNIT_TYPE_LABELS)
                  .filter(([value]) => value !== 'CLUB')
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label="Übergeordneter Bereich">
            {(id) => <OrgUnitSelect id={id} value={parentId} onChange={setParentId} />}
          </Field>
        </div>
      </Dialog>
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Bereich bearbeiten"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Button>
            <Button loading={update.isPending} disabled={!editName} onClick={() => update.mutate()}>Speichern</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Field label="Name" required>{(id) => <Input id={id} value={editName} onChange={(event) => setEditName(event.target.value)} />}</Field>
          <Field label="Art">{(id) => <Select id={id} value={editType} onChange={(event) => setEditType(event.target.value)}>{Object.entries(ORG_UNIT_TYPE_LABELS).filter(([value]) => value !== 'CLUB').map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select>}</Field>
          <Field label="Übergeordneter Bereich">{(id) => <OrgUnitSelect id={id} value={editParentId} onChange={setEditParentId} allowEmpty />}</Field>
        </div>
      </Dialog>
    </>
  );
}

/* ----------------------------------------------------------------- Personen */

function PersonsTab(): JSX.Element {
  const { can } = useAuth();
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [personDialog, setPersonDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [syncDialog, setSyncDialog] = useState(false);
  const [roleFor, setRoleFor] = useState<PersonDto | null>(null);
  // Standardmaessig wird der im Kopf gewaehlte Bereich beruecksichtigt.
  const [onlyCurrentScope, setOnlyCurrentScope] = useState(true);
  // Wie am Server: Struktur bearbeiten UND Rechte verwalten.
  const darfFunktionen = can('organisation', 'update') && can('permissions', 'manage');

  const personQuery = {
    ...(search.length >= 2 ? { search } : {}),
    ...(onlyCurrentScope && scope.unitId
      ? { organisationUnitId: scope.unitId, includeChildren: scope.includeChildren }
      : {}),
  };

  const [page, setPage] = useState(1);
  // Bei geaenderter Suche oder Bereichswahl wieder auf Seite 1.
  useEffect(() => setPage(1), [search, onlyCurrentScope, scope.unitId]);

  const seite = useQuery({
    queryKey: ['persons', personQuery, page],
    queryFn: () => api.get<Paged<PersonDto>>('/api/persons', { ...personQuery, page }),
    // Beim Tippen die bisherige Liste stehen lassen, sonst flackert die Seite.
    placeholderData: (vorher) => vorher,
  });
  const { isLoading, error } = seite;
  const data = seite.data?.items;

  /** Gruppierung nach Funktion, damit Sportler und Funktionaere getrennt sichtbar sind. */
  const groups = [
    { key: 'funktion', label: 'Funktionäre', match: (r: string) => !['spieler', 'mitglied'].includes(r) },
    { key: 'spieler', label: 'Spieler und Sportler', match: (r: string) => r === 'spieler' },
    { key: 'mitglied', label: 'Mitglieder', match: (r: string) => r === 'mitglied' },
  ];

  const grouped = groups.map((group) => ({
    ...group,
    persons: (data ?? []).filter((person) => {
      const keys = person.roles.filter((r) => r.isCurrent).map((r) => r.roleKey);
      if (keys.length === 0) return group.key === 'mitglied';
      return keys.some(group.match);
    }),
  }));

  const withoutRole = (data ?? []).filter((p) => p.roles.length === 0);

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleDto[]>('/api/roles'),
    // Archivierte Funktionen werden nicht mehr vergeben.
    select: (list) => list.filter((role) => !role.archived),
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  /** Gesetzt, wenn eine vorhandene Person bearbeitet wird. */
  const [personEdit, setPersonEdit] = useState<PersonDto | null>(null);

  const openPersonDialog = (person: PersonDto | null): void => {
    setPersonEdit(person);
    setFirstName(person?.firstName ?? '');
    setLastName(person?.lastName ?? '');
    setEmail(person?.email ?? '');
    setPhone(person?.phone ?? '');
    setPersonDialog(true);
  };

  const createPerson = useMutation({
    mutationFn: () => {
      const daten = { firstName, lastName, email: email || null, phone: phone || null };
      return personEdit
        ? api.patch<PersonDto>(`/api/persons/${personEdit.id}`, daten)
        : api.post<PersonDto>('/api/persons', daten);
    },
    onSuccess: async (neu) => {
      const warAnlegen = !personEdit;
      await queryClient.invalidateQueries({ queryKey: ['persons'] });
      toast.show(warAnlegen ? 'Person angelegt – weise jetzt eine Funktion zu' : 'Person gespeichert');
      setPersonEdit(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setPersonDialog(false);
      if (warAnlegen && neu?.id) navigate(`/personen/${neu.id}`);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen', 'error'),
  });

  const [roleId, setRoleId] = useState('');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [inherits, setInherits] = useState(true);

  const assignRole = useMutation({
    mutationFn: () =>
      api.post('/api/person-roles', {
        personId: roleFor?.id,
        roleId,
        organisationUnitId: unitId,
        inheritsToChildren: inherits,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['persons'] });
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      toast.show('Funktion zugewiesen');
      setRoleFor(null);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Zuweisen fehlgeschlagen', 'error'),
  });

  // Kein vorzeitiges return fuer Laden/Fehler: das wuerde das Suchfeld
  // aushaengen und bei jedem Tastendruck den Fokus nehmen.

  const renderPerson = (person: PersonDto): JSX.Element => (
          <li key={person.id}>
            <Card>
              <CardBody className="space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/personen/${person.id}`}
                      className="font-medium text-slate-900 hover:text-brand-700 hover:underline"
                    >
                      {person.displayName}
                    </Link>
                    <p className="text-sm text-slate-500">
                      {[person.email, person.phone, person.playerNumber ? `Spielernr. ${person.playerNumber}` : null].filter(Boolean).join(' · ') || 'Keine Kontaktdaten'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {person.hasLogin && <Badge tone="info">Zugang</Badge>}
                    {darfFunktionen && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setRoleFor(person);
                          setRoleId(roles?.[0]?.id ?? '');
                          setUnitId(scope.unitId ?? scope.units[0]?.id ?? null);
                        }}
                      >
                        Funktion
                      </Button>
                    )}
                  </div>
                </div>
                {person.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {person.roles.map((role) => (
                      <span key={role.id} className="inline-flex items-center gap-1">
                        <Badge tone={role.isCurrent ? 'brand' : 'neutral'}>
                          {role.roleName} · {role.organisationUnitName}
                          {!role.inheritsToChildren && ' (nur hier)'}
                          {role.validUntil && ` bis ${formatDate(role.validUntil)}`}
                        </Badge>
                        <RoleActions role={role} />
                      </span>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </li>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Person suchen …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1"
            />
            {can('persons', 'create') && (
              <>
                <Button variant="secondary" onClick={() => setImportDialog(true)}>
                  Importieren
                </Button>
                {can('persons', 'update') && (
                  <Button variant="secondary" onClick={() => setSyncDialog(true)}>
                    Verbandsliste abgleichen
                  </Button>
                )}
                <Button onClick={() => openPersonDialog(null)}>+ Person</Button>
              </>
            )}
          </div>
          {scope.unitId && (
            <Checkbox
              label={`Nur Personen aus "${scope.unit?.name ?? ''}"${
                scope.includeChildren ? ' und untergeordneten Bereichen' : ''
              }`}
              checked={onlyCurrentScope}
              onChange={setOnlyCurrentScope}
            />
          )}
          <p className="text-xs text-slate-500">
            Eine Person gehört über ihre Funktion zu einem Bereich – zum Beispiel als
            Spieler der U16 oder als Trainer der Kampfmannschaft. Über „Funktion" lassen sich
            Sportler, Mitglieder und Funktionäre einem Bereich zuordnen.
          </p>
        </CardBody>
      </Card>

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}

      {data && data.length === 0 && (
        <EmptyState
          title="Keine Personen gefunden"
          description={
            scope.unitId && onlyCurrentScope
              ? 'In diesem Bereich ist noch niemand zugeordnet. Personen gehören über ihre Funktion zu einem Bereich.'
              : undefined
          }
          action={
            scope.unitId && onlyCurrentScope ? (
              <Button variant="secondary" onClick={() => setOnlyCurrentScope(false)}>
                Ohne Bereichsfilter suchen
              </Button>
            ) : undefined
          }
        />
      )}

      {grouped.map((group) =>
        group.persons.length === 0 ? null : (
          <div key={group.key}>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-500">
              {group.label}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {group.persons.length}
              </span>
            </h3>
            <ul className="space-y-2">{group.persons.map(renderPerson)}</ul>
          </div>
        ),
      )}

      {seite.data && (
        <Pagination
          page={seite.data.page}
          pageSize={seite.data.pageSize}
          total={seite.data.total}
          onPageChange={setPage}
        />
      )}

      {withoutRole.length > 0 && (
        <p className="text-xs text-slate-500">
          {withoutRole.length} Person(en) ohne Funktion – bitte einem Bereich zuordnen.
        </p>
      )}

      <PersonImportDialog open={importDialog} onClose={() => setImportDialog(false)} />
      <PlayerSyncDialog open={syncDialog} onClose={() => setSyncDialog(false)} />

      <Dialog
        open={personDialog}
        onClose={() => {
          setPersonEdit(null);
          setPersonDialog(false);
        }}
        title={personEdit ? `${personEdit.displayName} bearbeiten` : 'Neue Person'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setPersonEdit(null);
                setPersonDialog(false);
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => createPerson.mutate()}
              loading={createPerson.isPending}
              disabled={!firstName || !lastName}
            >
              {personEdit ? 'Speichern' : 'Anlegen'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" required>
              {(id) => (
                <Input id={id} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              )}
            </Field>
            <Field label="Nachname" required>
              {(id) => (
                <Input id={id} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              )}
            </Field>
          </div>
          <Field label="E-Mail">
            {(id) => (
              <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            )}
          </Field>
          <Field label="Telefon">
            {(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} />}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={roleFor !== null}
        onClose={() => setRoleFor(null)}
        title={`Funktion für ${roleFor?.displayName ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRoleFor(null)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => assignRole.mutate()}
              loading={assignRole.isPending}
              disabled={!roleId || !unitId}
            >
              Zuweisen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Funktion" required>
            {(id) => (
              <Select id={id} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                <option value="">Bitte wählen …</option>
                {(roles ?? []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Bereich" required>
            {(id) => <OrgUnitSelect id={id} value={unitId} onChange={setUnitId} />}
          </Field>
          <Checkbox
            label="Gilt auch für untergeordnete Bereiche"
            checked={inherits}
            onChange={setInherits}
          />
        </div>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------------------- Organigramm */

function OrgChartTab(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<OrgChartNode['functions'][number] | null>(null);
  const [editRoleId, setEditRoleId] = useState('');
  const [editUnitId, setEditUnitId] = useState<string | null>(null);
  const [editInherits, setEditInherits] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['organisation', 'chart'],
    queryFn: () => api.get<OrgChartNode[]>('/api/organisation/chart'),
  });
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleDto[]>('/api/roles'),
    // Archivierte Funktionen werden nicht mehr vergeben.
    select: (list) => list.filter((role) => !role.archived),
  });

  const update = useMutation({
    mutationFn: () => api.patch(`/api/person-roles/${editing?.personRoleId}`, {
      roleId: editRoleId,
      organisationUnitId: editUnitId,
      inheritsToChildren: editInherits,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      await queryClient.invalidateQueries({ queryKey: ['persons'] });
      toast.show('Organigramm gespeichert');
      setEditing(null);
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Organigramm konnte nicht gespeichert werden', 'error'),
  });

  const startEdit = (fn: OrgChartNode['functions'][number]): void => {
    setEditing(fn);
    setEditRoleId(fn.roleId);
    setEditUnitId(fn.organisationUnitId);
    setEditInherits(fn.inheritsToChildren);
  };

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;

  const renderNode = (node: OrgChartNode, depth: number): JSX.Element => (
    <div key={node.organisationUnitId} className={depth > 0 ? 'ml-4 border-l border-slate-200 pl-4' : ''}>
      <div className="mb-2 rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium text-slate-900">{node.name}</p>
          <Badge tone="neutral">{ORG_UNIT_TYPE_LABELS[node.type] ?? node.type}</Badge>
        </div>
        {node.functions.length > 0 ? (
          <ul className="mt-2 space-y-0.5">
            {node.functions.map((fn) => (
              <li key={fn.personRoleId} className="flex items-center justify-between gap-2 text-sm text-slate-600">
                <span><span className="font-medium text-slate-700">{fn.roleName}</span> · {fn.personName}</span>
                <span className="flex shrink-0 gap-1">
                  {can('organisation', 'update') && can('permissions', 'manage') && (
                    <Button size="sm" variant="ghost" onClick={() => startEdit(fn)}>Bearbeiten</Button>
                  )}
                  <RoleActions role={{ id: fn.personRoleId, roleName: fn.roleName, isCurrent: true }} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-slate-400">Keine Funktion hinterlegt</p>
        )}
      </div>
      {node.children.map((child) => renderNode(child, depth + 1))}
    </div>
  );

  return (
    <>
      <p className="mb-3 text-sm text-slate-500">
        Das Organigramm zeigt ausschließlich organisatorische Zuständigkeiten. Zusätzlich
        vergebene Zugriffsrechte verändern die Darstellung nicht.
      </p>
      {(data ?? []).map((node) => renderNode(node, 0))}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={`Funktion für ${editing?.personName ?? ''} bearbeiten`}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Button>
            <Button loading={update.isPending} disabled={!editRoleId || !editUnitId} onClick={() => update.mutate()}>Speichern</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Field label="Funktion" required>
            {(id) => (
              <Select id={id} value={editRoleId} onChange={(event) => setEditRoleId(event.target.value)}>
                <option value="">Bitte wählen …</option>
                {(roles ?? []).map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
              </Select>
            )}
          </Field>
          <Field label="Bereich" required>
            {(id) => <OrgUnitSelect id={id} value={editUnitId} onChange={setEditUnitId} />}
          </Field>
          <Checkbox label="Gilt auch für untergeordnete Bereiche" checked={editInherits} onChange={setEditInherits} />
        </div>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ Vorlagen */

interface TemplateRowDraft {
  key: number;
  offsetDays: string;
  title: string;
  assigneePersonIds: string[];
  defaultRoleId: string | null;
  /** Ein Punkt pro Zeile. */
  checklist: string;
  attachments: AttachmentChoice[];
  extrasOpen: boolean;
}

let nextTemplateRowKey = 1;

const checklistLines = (text: string): string[] =>
  text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

function templateRow(values: Partial<Omit<TemplateRowDraft, 'key'>> = {}): TemplateRowDraft {
  return {
    key: nextTemplateRowKey++,
    offsetDays: values.offsetDays ?? '0',
    title: values.title ?? '',
    assigneePersonIds: values.assigneePersonIds ?? [],
    defaultRoleId: values.defaultRoleId ?? null,
    checklist: values.checklist ?? '',
    attachments: values.attachments ?? [],
    extrasOpen: values.extrasOpen ?? false,
  };
}

function TemplatesTab(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TaskTemplateDto | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState<TemplateRowDraft[]>([templateRow()]);

  const { data, isLoading } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => api.get<TaskTemplateDto[]>('/api/task-templates'),
  });

  const openCreate = (): void => {
    setEditing(null);
    setName('');
    setCategory('');
    setRows([templateRow()]);
    setDialogOpen(true);
  };

  const openEdit = (template: TaskTemplateDto): void => {
    setEditing(template);
    setName(template.name);
    setCategory(template.category ?? '');
    setRows(
      template.items.map((item) =>
        templateRow({
          offsetDays: String(item.offsetDays),
          title: item.title,
          assigneePersonIds: item.assignees.map((person) => person.id),
          defaultRoleId: item.defaultRoleId,
          checklist: item.checklist.join('\n'),
          attachments: toChoices(item.attachments),
        }),
      ),
    );
    setDialogOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        category: category || null,
        items: rows.map((row, index) => ({
          title: row.title,
          offsetDays: Number(row.offsetDays),
          priority: 'NORMAL',
          assigneePersonIds: row.assigneePersonIds,
          defaultRoleId: row.defaultRoleId,
          sortOrder: index,
          checklist: checklistLines(row.checklist),
          attachments: toRefs(row.attachments),
        })),
      };
      return editing
        ? api.patch(`/api/task-templates/${editing.id}`, payload)
        : api.post('/api/task-templates', payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task-templates'] });
      toast.show(editing ? 'Vorlage gespeichert' : 'Vorlage angelegt');
      setDialogOpen(false);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/task-templates/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['task-templates'] });
      toast.show('Vorlage entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Vorlage konnte nicht entfernt werden', 'error'),
  });

  const updateRow = (index: number, patch: Partial<TemplateRowDraft>): void => {
    setRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    );
  };

  const pasteRows = (index: number, event: ClipboardEvent<HTMLInputElement>): void => {
    const text = event.clipboardData.getData('text');
    if (!/\r?\n/.test(text)) return;
    const parsed = parseTemplateRows(text, Number(rows[index]?.offsetDays ?? 0));
    if (parsed.length === 0) return;
    event.preventDefault();
    const assignees = rows[index]?.assigneePersonIds ?? [];
    setRows((current) => [
      ...current.slice(0, index),
      ...parsed.map((row) =>
        templateRow({
          offsetDays: String(row.offsetDays),
          title: row.title,
          assigneePersonIds: assignees,
          defaultRoleId: rows[index]?.defaultRoleId ?? null,
        }),
      ),
      ...current.slice(index + 1),
    ]);
  };

  const validRows =
    rows.length > 0 &&
    rows.every(
      (row) =>
        row.title.trim() &&
        Number.isInteger(Number(row.offsetDays)) &&
        Number(row.offsetDays) >= -365 &&
        Number(row.offsetDays) <= 365,
    );

  if (isLoading) return <LoadingBlock />;

  return (
    <div className="space-y-3">
      {can('tasks', 'manage') && (
        <div className="flex justify-end">
          <Button onClick={openCreate}>+ Vorlage</Button>
        </div>
      )}

      {(data ?? []).map((template) => (
        <Card key={template.id}>
          <CardBody>
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">{template.name}</p>
                {template.description && (
                  <p className="text-sm text-slate-500">{template.description}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {template.category && <Badge tone="info">{template.category}</Badge>}
                {can('tasks', 'manage') && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(template)}>
                      Bearbeiten
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Vorlage "${template.name}" entfernen?`)) {
                          remove.mutate(template.id);
                        }
                      }}
                    >
                      Entfernen
                    </Button>
                  </>
                )}
              </div>
            </div>
            <ul className="space-y-1 text-sm">
              {template.items.map((item) => (
                <li key={item.id} className="flex flex-wrap justify-between gap-x-3 text-slate-600">
                  <span>{item.title}</span>
                  <span className="text-xs text-slate-500">
                    {item.offsetDays === 0
                      ? 'am Tag selbst'
                      : item.offsetDays < 0
                        ? `${Math.abs(item.offsetDays)} Tage vorher`
                        : `${item.offsetDays} Tage danach`}
                  </span>
                  {(item.assignees.length > 0 || item.defaultRoleName) && (
                    <span className="w-full text-xs text-slate-500">
                      Bearbeiter: {item.assignees.map((person) => person.name).join(', ') || item.defaultRoleName}
                    </span>
                  )}
                  {(item.checklist.length > 0 || item.attachments.length > 0) && (
                    <span className="w-full text-xs text-slate-500">
                      {[
                        item.checklist.length > 0 ? `☑ ${item.checklist.length} Checklistenpunkt(e)` : null,
                        item.attachments.length > 0 ? `📎 ${item.attachments.map((a) => a.title).join(', ')}` : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ))}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!name.trim() || !validRows}
            >
              Speichern
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" required>
              {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
            </Field>
            <Field label="Kategorie">
              {(id) => (
                <Input id={id} value={category} onChange={(e) => setCategory(e.target.value)} />
              )}
            </Field>
          </div>

          <div className="space-y-2">
            <div>
              <p className="text-sm font-medium text-slate-700">Aufgaben</p>
              <p className="text-xs text-slate-500">
                Eine Zeile je Aufgabe. Mehrzeiligen Text in eine Bezeichnung einzufügen erzeugt automatisch mehrere Zeilen; „-2. Aufgabe“ setzt zugleich die Tage.
              </p>
            </div>
            {rows.map((row, index) => (
              <div
                key={row.key}
                className="grid gap-2 rounded-xl border border-slate-200 p-3 md:grid-cols-[6rem_minmax(12rem,1fr)_minmax(14rem,1fr)_auto] md:items-start"
              >
                <Field label="Tage" required>
                  {(id) => (
                    <Input
                      id={id}
                      type="number"
                      min={-365}
                      max={365}
                      value={row.offsetDays}
                      onChange={(event) => updateRow(index, { offsetDays: event.target.value })}
                    />
                  )}
                </Field>
                <Field label="Bezeichnung" required>
                  {(id) => (
                    <Input
                      id={id}
                      value={row.title}
                      onChange={(event) => updateRow(index, { title: event.target.value })}
                      onPaste={(event) => pasteRows(index, event)}
                      placeholder="z. B. Helfer organisieren"
                    />
                  )}
                </Field>
                <Field label="Bearbeiter (1 … n)">
                  {(id) => (
                    <PersonPicker
                      id={id}
                      value={row.assigneePersonIds}
                      onChange={(value) => updateRow(index, { assigneePersonIds: value })}
                    />
                  )}
                </Field>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={rows.length === 1}
                  onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                >
                  Zeile entfernen
                </Button>
                <div className="md:col-span-4">
                  <button
                    type="button"
                    aria-expanded={row.extrasOpen}
                    onClick={() => updateRow(index, { extrasOpen: !row.extrasOpen })}
                    className="text-sm font-medium text-brand-700 hover:underline"
                  >
                    {row.extrasOpen ? 'Checkliste & Anhänge ausblenden' : 'Checkliste & Anhänge'}
                    {!row.extrasOpen && (checklistLines(row.checklist).length > 0 || row.attachments.length > 0) && (
                      <span className="ml-1 text-slate-500">
                        ({checklistLines(row.checklist).length} Punkte, {row.attachments.length} Anhänge)
                      </span>
                    )}
                  </button>
                  {row.extrasOpen && (
                    <div className="mt-2 grid gap-3 rounded-lg bg-slate-50 p-3 md:grid-cols-2">
                      <Field label="Checkliste" hint="Ein Punkt pro Zeile – jede erzeugte Aufgabe erhält diese Punkte">
                        {(id) => (
                          <Textarea
                            id={id}
                            rows={4}
                            value={row.checklist}
                            onChange={(event) => updateRow(index, { checklist: event.target.value })}
                          />
                        )}
                      </Field>
                      <Field label="Anhänge" hint="Werden an jede erzeugte Aufgabe angehängt">
                        {(id) => (
                          <AttachmentPicker
                            id={id}
                            value={row.attachments}
                            onChange={(value) => updateRow(index, { attachments: value })}
                          />
                        )}
                      </Field>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <Button variant="secondary" size="sm" onClick={() => setRows((current) => [...current, templateRow()])}>
              + Zeile
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

/* ---------------------------------------------------------- Automatisierungen */

const TRIGGER_LABELS: Record<string, string> = {
  MATCH_CREATED: 'Neues Spiel wurde übernommen',
  MATCH_CHANGED: 'Spieltermin hat sich geändert',
  SPONSORSHIP_EXPIRING: 'Sponsorvertrag läuft aus',
};

const TRIGGER_HINTS: Record<string, string> = {
  MATCH_CREATED:
    'Sobald ein Spiel vom Verband übernommen oder angelegt wird, entstehen die Aufgaben aus der Vorlage.',
  MATCH_CHANGED: 'Wird ein Spieltermin verschoben, laufen dieselben Schritte erneut.',
  SPONSORSHIP_EXPIRING:
    'Steuert, wie viele Tage vor Vertragsende an die Verlängerung erinnert wird.',
};

/** Ausloeser, die Vorlage und Spiel-Einstellungen verwenden. */
const MATCH_TRIGGERS = ['MATCH_CREATED', 'MATCH_CHANGED'];

function AutomationsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayEdit = can('tasks', 'manage');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AutomationRuleDto | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['automations'],
    queryFn: () => api.get<AutomationRuleDto[]>('/api/automations'),
  });

  const { data: templates } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => api.get<TaskTemplateDto[]>('/api/task-templates'),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['automations'] });
  };

  const toggle = useMutation({
    mutationFn: (rule: AutomationRuleDto) =>
      api.patch(`/api/automations/${rule.id}`, {
        trigger: rule.trigger,
        organisationUnitId: rule.organisationUnitId,
        templateId: rule.templateId,
        isActive: !rule.isActive,
        config: rule.config,
      }),
    onSuccess: async () => {
      await refresh();
      toast.show('Automatisierung geändert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Änderung fehlgeschlagen', 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/automations/${id}`),
    onSuccess: async () => {
      await refresh();
      toast.show('Automatisierung entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;

  return (
    <div className="space-y-3">
      <Card>
        <CardBody>
          <SectionTitle
            title="Automatisierungen"
            subtitle="Wiederkehrende Vereinsarbeit läuft von selbst an – jederzeit abschaltbar"
            action={
              mayEdit ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                >
                  + Automatisierung
                </Button>
              ) : undefined
            }
          />
          {(data ?? []).length === 0 && (
            <p className="text-sm text-slate-500">
              Es ist keine Automatisierung eingerichtet. Alle Aufgaben werden von Hand angelegt.
            </p>
          )}
        </CardBody>
      </Card>

      {(data ?? []).map((rule) => (
        <Card key={rule.id} className={rule.isActive ? undefined : 'opacity-70'}>
          <CardBody className="space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">
                  {TRIGGER_LABELS[rule.trigger] ?? rule.trigger}
                </p>
                <p className="text-sm text-slate-500">{TRIGGER_HINTS[rule.trigger] ?? ''}</p>
              </div>
              <Badge tone={rule.isActive ? 'success' : 'neutral'}>
                {rule.isActive ? 'Aktiv' : 'Abgeschaltet'}
              </Badge>
            </div>

            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Bereich</dt>
                <dd className="text-slate-700">
                  {rule.organisationUnitName ?? 'Alle Bereiche'}
                </dd>
              </div>
              {MATCH_TRIGGERS.includes(rule.trigger) && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">Vorlage</dt>
                  <dd className="text-slate-700">
                    {rule.templateName ?? 'Keine – es entstehen keine Aufgaben'}
                  </dd>
                </div>
              )}
            </dl>

            <div className="flex flex-wrap gap-1">
              {MATCH_TRIGGERS.includes(rule.trigger) ? (
                <>
                  <Badge tone={rule.config.onlyHomeMatches ? 'info' : 'neutral'}>
                    {rule.config.onlyHomeMatches ? 'Nur Heimspiele' : 'Alle Spiele'}
                  </Badge>
                  {rule.config.createMediaPost && <Badge tone="info">Medienbeitrag</Badge>}
                  {rule.config.checkSponsoring && <Badge tone="info">Sponsoring prüfen</Badge>}
                </>
              ) : (
                <Badge tone="info">{rule.config.leadDays} Tage Vorlauf</Badge>
              )}
            </div>

            {mayEdit && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => toggle.mutate(rule)}
                  loading={toggle.isPending}
                >
                  {rule.isActive ? 'Abschalten' : 'Einschalten'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditing(rule);
                    setDialogOpen(true);
                  }}
                >
                  Bearbeiten
                </Button>
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-rose-600"
                  onClick={() => {
                    if (window.confirm('Automatisierung wirklich entfernen?')) {
                      remove.mutate(rule.id);
                    }
                  }}
                >
                  entfernen
                </button>
              </div>
            )}
          </CardBody>
        </Card>
      ))}

      <AutomationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        rule={editing}
        templates={templates ?? []}
      />
    </div>
  );
}

function AutomationDialog({
  open,
  onClose,
  rule,
  templates,
}: {
  open: boolean;
  onClose: () => void;
  rule: AutomationRuleDto | null;
  templates: TaskTemplateDto[];
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isEdit = !!rule;

  const [trigger, setTrigger] = useState('MATCH_CREATED');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [onlyHomeMatches, setOnlyHomeMatches] = useState(true);
  const [createMediaPost, setCreateMediaPost] = useState(true);
  const [checkSponsoring, setCheckSponsoring] = useState(true);
  const [leadDays, setLeadDays] = useState('30');

  useEffect(() => {
    if (!open) return;
    if (rule) {
      setTrigger(rule.trigger);
      setUnitId(rule.organisationUnitId);
      setTemplateId(rule.templateId ?? '');
      setIsActive(rule.isActive);
      setOnlyHomeMatches(rule.config.onlyHomeMatches);
      setCreateMediaPost(rule.config.createMediaPost);
      setCheckSponsoring(rule.config.checkSponsoring);
      setLeadDays(String(rule.config.leadDays));
    } else {
      setTrigger('MATCH_CREATED');
      setUnitId(null);
      setTemplateId('');
      setIsActive(true);
      setOnlyHomeMatches(true);
      setCreateMediaPost(true);
      setCheckSponsoring(true);
      setLeadDays('30');
    }
  }, [open, rule]);

  const isMatchTrigger = MATCH_TRIGGERS.includes(trigger);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        trigger,
        organisationUnitId: unitId,
        templateId: isMatchTrigger ? templateId || null : null,
        isActive,
        config: {
          onlyHomeMatches,
          createMediaPost,
          checkSponsoring,
          leadDays: Number(leadDays) || 30,
        },
      };
      return isEdit
        ? api.patch(`/api/automations/${rule?.id}`, payload)
        : api.post('/api/automations', payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast.show(isEdit ? 'Automatisierung gespeichert' : 'Automatisierung angelegt');
      onClose();
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Automatisierung bearbeiten' : 'Neue Automatisierung'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Wann soll es auslösen?" required>
          {(id) => (
            <Select id={id} value={trigger} onChange={(e) => setTrigger(e.target.value)}>
              {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {TRIGGER_HINTS[trigger]}
        </p>

        <Field
          label="Für welchen Bereich?"
          hint="Ohne Auswahl gilt die Automatisierung für den ganzen Verein"
        >
          {(id) => (
            <OrgUnitSelect
              id={id}
              value={unitId}
              onChange={setUnitId}
              allowEmpty
              emptyLabel="Alle Bereiche"
            />
          )}
        </Field>

        {isMatchTrigger && (
          <>
            <Field label="Welche Aufgaben sollen entstehen?" hint="Vorlage mit den Arbeitsschritten">
              {(id) => (
                <Select id={id} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  <option value="">Keine – nur die übrigen Schritte ausführen</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.items.length} Schritte)
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <div className="space-y-2 rounded-xl border border-slate-300 p-3">
              <Checkbox
                label="Nur bei Heimspielen auslösen"
                checked={onlyHomeMatches}
                onChange={setOnlyHomeMatches}
              />
              <Checkbox
                label="Medienbeitrag als Entwurf vorbereiten"
                checked={createMediaPost}
                onChange={setCreateMediaPost}
              />
              <Checkbox
                label="Offene Matchsponsor-Leistungen prüfen"
                checked={checkSponsoring}
                onChange={setCheckSponsoring}
              />
            </div>
          </>
        )}

        {!isMatchTrigger && (
          <Field label="Vorlaufzeit in Tagen" required hint="So früh wird an die Verlängerung erinnert">
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                max="365"
                value={leadDays}
                onChange={(e) => setLeadDays(e.target.value)}
              />
            )}
          </Field>
        )}

        <Checkbox label="Automatisierung ist aktiv" checked={isActive} onChange={setIsActive} />
      </div>
    </Dialog>
  );
}

/* -------------------------------------------------------------- Verbindungen */

function IntegrationsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: google, isLoading } = useQuery({
    queryKey: ['integrations', 'google'],
    queryFn: () => api.get<IntegrationStatusDto>('/api/integrations/google-calendar'),
  });

  const { data: football } = useQuery({
    queryKey: ['integrations', 'football'],
    queryFn: () =>
      api.get<{
        provider: string;
        isLive: boolean;
        feed: { url: string; name: string; clubNumber: string | null } | null;
        teamMappings: Record<string, string>;
        lastRun: { status: string; startedAt: string; stats: Record<string, number> } | null;
      }>('/api/football/status'),
  });

  const { confirm, confirmDialog } = useConfirm();
  // Vereinskalender des Verbands: Adresse und Vereinsnummer.
  const [feedUrl, setFeedUrl] = useState('');
  const [feedNummer, setFeedNummer] = useState('');
  const [feedGeprueft, setFeedGeprueft] = useState<{ matchCount: number; homeCount: number; teams: { name: string; matchCount: number; competition: string | null }[] } | null>(null);
  useEffect(() => {
    setFeedUrl(football?.feed?.url ?? '');
    setFeedNummer(football?.feed?.clubNumber ?? '');
  }, [football?.feed?.url, football?.feed?.clubNumber]);

  const { data: teams } = useQuery({
    queryKey: ['integrations', 'football', 'teams'],
    queryFn: () =>
      api.get<{ externalId: string; name: string; competition: string | null }[]>(
        '/api/football/teams',
      ),
  });

  const { data: players } = useQuery({
    queryKey: ['integrations', 'football', 'players'],
    queryFn: () => api.get<ExternalPlayerDto[]>('/api/football/players'),
  });

  const { data: available } = useQuery({
    queryKey: ['integrations', 'google', 'available'],
    queryFn: () =>
      api.get<{ id: string; name: string; primary: boolean }[]>(
        '/api/integrations/google-calendar/available',
      ),
    enabled: google?.connected === true,
  });

  const connect = useMutation({
    mutationFn: () =>
      api.post('/api/integrations/google-calendar/connect', {
        displayName: 'Vereinskalender',
        accountRef: 'verein@example.at',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show('Kalenderkonto verbunden');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Verbinden fehlgeschlagen', 'error'),
  });

  const disconnect = useMutation({
    mutationFn: () => api.post('/api/integrations/google-calendar/disconnect'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show('Verbindung getrennt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Trennen fehlgeschlagen', 'error'),
  });

  const linkCalendar = useMutation({
    mutationFn: (calendar: { id: string; name: string }) =>
      api.post('/api/integrations/google-calendar/calendars', {
        externalCalendarId: calendar.id,
        name: calendar.name,
        direction: 'BIDIRECTIONAL',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show('Kalender verknüpft');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Kalender konnte nicht verknüpft werden', 'error'),
  });

  const setSync = useMutation({
    mutationFn: (enabled: boolean) => api.put('/api/integrations/google-calendar/enabled', { enabled }),
    onSuccess: async (_result, enabled) => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show(enabled ? 'Kalenderabgleich eingeschaltet' : 'Kalenderabgleich ausgeschaltet');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Umschalten fehlgeschlagen', 'error'),
  });

  const syncCalendar = useMutation({
    mutationFn: () => api.post<Record<string, number>>('/api/integrations/google-calendar/sync', {}),
    onSuccess: async (stats) => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.show(
        `Kalender abgeglichen: ${stats['imported'] ?? 0} importiert, ${stats['exported'] ?? 0} exportiert`,
      );
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Abgleich fehlgeschlagen', 'error'),
  });

  const mapTeam = useMutation({
    mutationFn: (input: { teamExternalId: string; organisationUnitId: string | null }) =>
      api.post('/api/football/team-mapping', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show('Zuordnung gespeichert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  const speichereFeed = useMutation({
    mutationFn: () =>
      api.put<{ matchCount: number; homeCount: number; teams: { name: string; matchCount: number; competition: string | null }[] }>(
        '/api/football/feed',
        { url: feedUrl.trim(), name: 'Vereinstermine', clubNumber: feedNummer.trim() || null },
      ),
    onSuccess: async (result) => {
      setFeedGeprueft(result);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show(`Kalender übernommen: ${result.matchCount} Termine, ${result.teams.length} Mannschaften`);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Kalender konnte nicht gelesen werden', 'error'),
  });

  const entferneFeed = useMutation({
    mutationFn: () => api.del('/api/football/feed'),
    onSuccess: async () => {
      setFeedGeprueft(null);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show('Kalender entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });

  const syncFootball = useMutation({
    mutationFn: () => api.post<Record<string, number>>('/api/football/sync', {}),
    onSuccess: async (stats) => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['matches'] });
      toast.show(`Spiele abgeglichen: ${stats['created'] ?? 0} neu, ${stats['updated'] ?? 0} geändert`);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Abgleich fehlgeschlagen', 'error'),
  });

  const linkPlayer = useMutation({
    mutationFn: (input: { playerId: string; personId: string | null }) =>
      api.post(`/api/football/players/${input.playerId}/link`, { personId: input.personId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.show('Verknüpfung gespeichert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Verknüpfung konnte nicht gespeichert werden', 'error'),
  });

  if (isLoading) return <LoadingBlock />;

  const feedEntfernen = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Vereinskalender entfernen?',
      message: 'Die Adresse des Verbandskalenders wird gelöscht. Spiele werden danach nicht mehr abgeglichen, bis du wieder eine Adresse einträgst.',
      confirmLabel: 'Ja, entfernen',
      tone: 'danger',
    });
    if (ok) entferneFeed.mutate();
  };

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Card>
        <CardBody className="space-y-3">
          <SectionTitle
            title="Kalender-Verbindung"
            subtitle={`Betriebsart: ${google?.mode === 'real' ? 'Google Calendar API' : 'lokaler Testmodus'}`}
          />
          {google?.mode !== 'real' && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Es ist noch kein Google-Konto hinterlegt. Der lokale Testmodus bildet den kompletten
              Abgleich nach, damit die Funktion ohne externe Zugangsdaten geprüft werden kann.
            </p>
          )}

          {/* Hauptschalter: ohne ihn verlaesst kein Termin den Verein. */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-sm">
              <span className="font-medium text-slate-800">Abgleich mit einem äußeren Kalender</span>
              <span className="block text-xs text-slate-500">
                {google?.syncEnabled
                  ? 'Eingeschaltet: Termine werden mit den verknüpften Kalendern abgeglichen.'
                  : 'Ausgeschaltet: Termine bleiben im Verein. Weder nachts noch von Hand wird abgeglichen.'}
              </span>
            </span>
            <Button
              variant={google?.syncEnabled ? 'secondary' : 'primary'}
              loading={setSync.isPending}
              onClick={() => setSync.mutate(!google?.syncEnabled)}
            >
              {google?.syncEnabled ? 'Ausschalten' : 'Einschalten'}
            </Button>
          </div>

          {!google?.connected ? (
            <Button onClick={() => connect.mutate()} loading={connect.isPending}>
              Kalenderkonto verbinden
            </Button>
          ) : (
            <>
              <p className="text-sm text-slate-700">
                Verbunden als <span className="font-medium">{google.displayName}</span>
              </p>

              <div>
                <p className="label">Verknüpfte Kalender</p>
                {google.calendars.length === 0 ? (
                  <p className="text-sm text-slate-500">Noch kein Kalender verknüpft.</p>
                ) : (
                  <ul className="space-y-1">
                    {google.calendars.map((cal) => (
                      <li
                        key={cal.id}
                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm"
                      >
                        <span>{cal.name}</span>
                        <span className="text-xs text-slate-500">
                          {cal.direction} ·{' '}
                          {cal.lastSyncAt ? `zuletzt ${formatDateTime(cal.lastSyncAt)}` : 'noch nie'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {available && available.length > 0 && (
                <div>
                  <p className="label">Verfügbare Kalender</p>
                  <div className="flex flex-wrap gap-2">
                    {available
                      .filter(
                        (cal) => !google.calendars.some((c) => c.externalCalendarId === cal.id),
                      )
                      .map((cal) => (
                        <Button
                          key={cal.id}
                          variant="secondary"
                          size="sm"
                          onClick={() => linkCalendar.mutate({ id: cal.id, name: cal.name })}
                        >
                          + {cal.name}
                        </Button>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => syncCalendar.mutate()} loading={syncCalendar.isPending}>
                  Jetzt abgleichen
                </Button>
                <Button variant="secondary" onClick={() => disconnect.mutate()}>
                  Verbindung trennen
                </Button>
              </div>

              {google.lastRun && (
                <p className="text-xs text-slate-500">
                  Letzter Lauf: {formatDateTime(google.lastRun.startedAt)} · {google.lastRun.status}
                </p>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <SectionTitle
            title="Fußballverband"
            subtitle={`Datenquelle: ${football?.isLive ? football.provider : 'lokaler Testprovider'}`}
          />
          {!football?.isLive && !football?.feed && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Noch kein Kalender eingetragen – UK Info arbeitet mit lokalen Testdaten. Unten die
              Adresse des Vereinskalenders eintragen, dann kommen die echten Termine.
            </p>
          )}

          <div className="space-y-3 rounded-xl border border-slate-200 p-3">
            <p className="label">Vereinskalender des Verbands</p>
            <p className="hint">
              Die Adresse steht auf der ÖFB-Vereinshomepage unter „Termine“ als Kalender-Abo
              (<code>webcal://…-T.ics</code>). Der Kalender mit allen Mannschaften genügt – UK Info
              teilt ihn selbst nach Mannschaft auf.
            </p>
            <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
              <Field label="Adresse des Kalenders">
                {(id) => (
                  <Input
                    id={id}
                    value={feedUrl}
                    placeholder="https://www.fussballoesterreich.at/netzwerk/icalendar/…-T.ics"
                    onChange={(e) => setFeedUrl(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Vereinsnummer" hint="Im Kalender als X-HOMENR">
                {(id) => (
                  <Input id={id} value={feedNummer} inputMode="numeric" placeholder="z. B. 4131" onChange={(e) => setFeedNummer(e.target.value)} />
                )}
              </Field>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!feedUrl.trim()}
                loading={speichereFeed.isPending}
                onClick={() => speichereFeed.mutate()}
              >
                Kalender prüfen und übernehmen
              </Button>
              {football?.feed && (
                <Button variant="secondary" loading={entferneFeed.isPending} onClick={() => void feedEntfernen()}>
                  Kalender entfernen
                </Button>
              )}
            </div>
            {feedGeprueft && (
              <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <p>
                  {feedGeprueft.matchCount} Termine gelesen, davon {feedGeprueft.homeCount} Heimspiele.
                </p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {feedGeprueft.teams.map((team) => (
                    <li key={team.name}>
                      {team.name}: {team.matchCount} Termine{team.competition ? ` · ${team.competition}` : ''}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs">
                  Mit „Spiele und Kader abgleichen“ werden sie übernommen. Mannschaften, die es hier
                  noch nicht gibt, erscheinen danach unter Sport → Mannschaften.
                </p>
              </div>
            )}
          </div>

          <div>
            <p className="label">Mannschaften zuordnen</p>
            <ul className="space-y-2">
              {(teams ?? []).map((team) => (
                <li key={team.externalId} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-40 flex-1 text-sm text-slate-700">
                    {team.name}
                    {team.competition && (
                      <span className="text-slate-400"> · {team.competition}</span>
                    )}
                  </span>
                  <div className="w-56">
                    <OrgUnitSelect
                      value={football?.teamMappings[team.externalId] ?? null}
                      onChange={(unitId) =>
                        mapTeam.mutate({ teamExternalId: team.externalId, organisationUnitId: unitId })
                      }
                      allowEmpty
                      emptyLabel="Nicht zugeordnet"
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <Button onClick={() => syncFootball.mutate()} loading={syncFootball.isPending}>
            Spiele und Kader abgleichen
          </Button>

          {football?.lastRun && (
            <p className="text-xs text-slate-500">
              Letzter Lauf: {formatDateTime(football.lastRun.startedAt)} · {football.lastRun.status}{' '}
              · {football.lastRun.stats['created'] ?? 0} neu,{' '}
              {football.lastRun.stats['updated'] ?? 0} aktualisiert
            </p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle
            title="Kader des Verbands"
            subtitle="Verbandsspieler können mit Vereinspersonen verknüpft werden"
          />
          {(players ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Spielerdaten synchronisiert.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {(players ?? []).map((player) => (
                <li key={player.id} className="flex flex-wrap items-center gap-2 py-2.5">
                  <span className="min-w-40 flex-1 text-sm">
                    <span className="font-medium text-slate-800">
                      {player.firstName} {player.lastName}
                    </span>
                    <span className="text-slate-500">
                      {player.shirtNumber ? ` · Nr. ${player.shirtNumber}` : ''}
                      {player.position ? ` · ${player.position}` : ''}
                    </span>
                  </span>
                  <div className="w-56">
                    <PersonSelect
                      value={player.linkedPersonId ?? null}
                      onChange={(personId) => linkPlayer.mutate({ playerId: player.id, personId })}
                      placeholder="Nicht verknüpft"
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/* ----------------------------------------------------------------- Protokoll */

function AuditTab(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: () => api.get<AuditLogDto[]>('/api/audit-logs', { limit: 100 }),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;

  const ENTITY_LABELS: Record<string, string> = {
    task: 'Aufgabe',
    calendar_event: 'Termin',
    person: 'Person',
    person_role: 'Funktion',
    permission_grant: 'Zugriffsrecht',
    organisation_unit: 'Bereich',
    sponsorship: 'Sponsorvertrag',
    sponsor: 'Sponsor',
    document: 'Dokument',
    media_post: 'Medienbeitrag',
    media_asset: 'Medium',
    match: 'Spiel',
    task_template: 'Vorlage',
    automation_rule: 'Automatisierung',
    external_player: 'Verbandsspieler',
  };

  const ACTION_TEXT: Record<string, string> = {
    create: 'angelegt',
    update: 'geändert',
    delete: 'gelöscht',
    assign: 'zugewiesen',
    end: 'beendet',
    apply: 'angewendet',
    transition: 'Status geändert',
    link: 'verknüpft',
    unlink: 'Verknüpfung entfernt',
  };

  return (
    <Card>
      <CardBody>
        <SectionTitle title="Änderungsprotokoll" subtitle="Die letzten 100 kritischen Änderungen" />
        {(data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Einträge.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(data ?? []).map((log) => (
              <li key={log.id} className="py-2">
                <p className="text-sm text-slate-800">
                  <span className="font-medium">
                    {ENTITY_LABELS[log.entityType] ?? log.entityType}
                  </span>{' '}
                  {ACTION_TEXT[log.action] ?? log.action}
                  {log.actorName ? ` von ${log.actorName}` : ''}
                </p>
                <p className="text-xs text-slate-500">{formatDateTime(log.createdAt)}</p>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
